import { useRef } from 'react';
import { Navigate, useParams } from 'react-router-dom';

import { Body1, Body2, Stack } from '@myphx-engineering/phx-design-system';

import { useMedicationConfigOverride } from '../../../contexts/DrugProgramProvider';
import { PrescriberOrderStatus } from '../../../graphql/generated/graphql';
import { usePatientContext } from '../../../hooks/use-patient-context';
import { getPricingByNdc } from '../../../util/config/get-pricing';
import { getAbsoluteRoute } from '../../../util/get-absolute-route';
import { Loader } from '../../common/Loader';
import { ConditionalComponent } from '../../utility/ConditionalComponent';
import MedicationPricing from '../order-details/MedicationPricing';

import { KeepCurrentPrescriptionButton } from './KeepCurrentPrescriptionButton';
import { RequestSwitchButton } from './RequestSwitchButton';
import { useSwitchPrescription } from './shared';
import { SwitchOtherOptions } from './SwitchOtherOptions';
import { SwitchTargetHeader } from './SwitchTargetHeader';

export const SwitchVerificationScreen = () => {
    const { targetNdc } = useParams();
    const configOverride = useMedicationConfigOverride();
    const { patient } = usePatientContext();
    const {
        order,
        orderId,
        switchTarget,
        medication,
        medicationLoading,
        switchTargetNdcDefinition,
    } = useSwitchPrescription();

    /*
     * We only want to redirect when the _initial_ status of the order is
     * `ON_HOLD`. We can't just look at the order directly, because it gets
     * modified on this step, and this redirect would trigger before the one
     * that sends the user to the confirmation.
     */
    const originalOrderStatus = useRef(order.status);
    if (originalOrderStatus.current === PrescriberOrderStatus.OnHold) {
        return (
            <Navigate
                replace
                to={getAbsoluteRoute('order.prescriberOrderId.details', {
                    prescriberOrderId: orderId,
                })}
            />
        );
    }

    if (medicationLoading) {
        return <Loader />;
    }

    if (!targetNdc || !switchTarget) {
        return <Navigate to={getAbsoluteRoute('error')} />;
    }

    const switchDrugPricing = getPricingByNdc(configOverride, targetNdc);

    return (
        <Stack gap="lg">
            <SwitchTargetHeader option={switchTarget} />
            <Stack gap="xs">
                {switchTarget.description.map((line) => (
                    <Body1 key={line}>
                        <span dangerouslySetInnerHTML={{ __html: line }} />
                    </Body1>
                ))}
            </Stack>
            <ConditionalComponent condition={Boolean(switchDrugPricing)}>
                <MedicationPricing
                    pricingInfo={switchDrugPricing!}
                    medication={medication}
                    ndcDefinition={switchTargetNdcDefinition}
                />
            </ConditionalComponent>
            <ConditionalComponent condition={Boolean(switchTarget.disclaimer)}>
                <Body2>
                    <span
                        dangerouslySetInnerHTML={{
                            __html: switchTarget.disclaimer!,
                        }}
                    />
                </Body2>
            </ConditionalComponent>

            <RequestSwitchButton
                orderId={orderId}
                patient={patient}
                targetNdc={targetNdc}
            />
            <KeepCurrentPrescriptionButton prescriberOrderId={orderId} />
            <SwitchOtherOptions order={order} />
        </Stack>
    );
};
